import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { Assert } from '../../assert';
import { useTranslations } from '../../translations/composables/useTranslations';
import {
  defaultBindings,
  KeyboardActions,
  type FormKeybind,
  type FormKeyboardAction,
  type Keybind,
  type KeyboardAction,
} from '../domain/keyboardActions';

export const useKeybindStore = defineStore('keybinds', () => {
  const translation = useTranslations();

  const mode = ref<'default' | 'edit' | 'edge' | 'none'>('none');
  const formData = ref<FormKeyboardAction[]>([]);
  const isLockedInitial = ref<boolean>(false);
  const isLocked = ref<boolean>(false);

  const keyboardAction = ref<KeyboardAction>({ action: null, timestamp: 0 });
  const bindings = ref<Record<KeyboardActions, Keybind>>();

  watch(
  () => translation.value,
  () => {
    for (const entry of formData.value) {
      entry.keybind.actionText = translation.value.enums.keyboardAction(entry.action);
    }
  },
);

  const keybindings = computed(() => {
    if (bindings.value) {
      return typeSafeObjectEntries(bindings.value);
    }
    return undefined;
  });

  function parseKeybindings(
    keybindString: string,
    locked: boolean,
    operation: 'default' | 'edit' | 'edge',
  ) {
    mode.value = operation;
    if ((operation === 'edit' || operation === 'edge') && keybindString.length > 0) {
      try {
        let result = JSON.parse(keybindString) as Record<KeyboardActions, Keybind>;
        result = syncRecordWithEnum(result);

        bindings.value = result;
        isLockedInitial.value = locked;
        isLocked.value = locked;

        updateFormData();
        return;
      } catch (e) {
        console.error(e);
      }
    }
    bindings.value = defaultBindings;
    isLockedInitial.value = false;
    isLocked.value = false;
    updateFormData();
  }

  const nonTextInputTypes = new Set(['checkbox', 'radio', 'button', 'submit', 'range', 'color']);

  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;

    if (target.isContentEditable) return true;
    if (target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') return true;
    if (target.tagName === 'INPUT') {
      const type = (target as HTMLInputElement).type;
      return !nonTextInputTypes.has(type);
    }

    return false;
  }

function normalizeKeyString(input: {
  code: string | undefined;
  ctrlKey: boolean | undefined;
  altKey: boolean | undefined;
  shiftKey: boolean | undefined;
  metaKey: boolean | undefined;
}): string {
  const code = (input.code ?? '').toUpperCase();
  const ctrl = input.ctrlKey ? '1' : '0';
  const alt = input.altKey ? '1' : '0';
  const shift = input.shiftKey ? '1' : '0';
  const meta = input.metaKey ? '1' : '0';
  return `${ctrl}|${alt}|${shift}|${meta}|${code}`;
}

// Recomputed only when bindings.value changes, not per keypress
const keybindLookup = computed(() => {
  const map = new Map<string, KeyboardActions>();
  if (!bindings.value) return map;

  for (const [action, keybind] of typeSafeObjectEntries(bindings.value)) {
    map.set(normalizeKeyString(keybind), action);
  }
  return map;
});

function findKeyboardAction(event: KeyboardEvent): KeyboardActions | null {
  const lookupKey = normalizeKeyString({
    code: event.code,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    shiftKey: event.shiftKey,
    metaKey: event.metaKey,
  });

  return keybindLookup.value.get(lookupKey) ?? null;
}

  const activeActions = ref<Set<KeyboardActions>>(new Set());

  function keyDownHandler(event: KeyboardEvent) {
    if (isTypingTarget(event.target)) return;

    const action = findKeyboardAction(event);
    if (!action) return;

    if (!event.repeat) {
      // fires exactly once per physical press-down, ignores OS auto-repeat
      keyboardAction.value = { action, timestamp: Date.now() };
    }
    activeActions.value.add(action); // Set mutation is reactive in Vue 3
  }

  function keyUpHandler(event: KeyboardEvent) {
    const action = findKeyboardAction(event);
    if (!action) return;
    activeActions.value.delete(action);
  }

  function isActionHeld(action: KeyboardActions): boolean {
    return activeActions.value.has(action);
  }

  function clearActiveActions() {
    activeActions.value.clear();
  }

  const numpadCodeLabels: Record<string, string> = {
  Numpad0: 'NUMPAD 0',
  Numpad1: 'NUMPAD 1',
  Numpad2: 'NUMPAD 2',
  Numpad3: 'NUMPAD 3',
  Numpad4: 'NUMPAD 4',
  Numpad5: 'NUMPAD 5',
  Numpad6: 'NUMPAD 6',
  Numpad7: 'NUMPAD 7',
  Numpad8: 'NUMPAD 8',
  Numpad9: 'NUMPAD 9',
  NumpadAdd: 'NUMPAD +',
  NumpadSubtract: 'NUMPAD -',
  NumpadMultiply: 'NUMPAD *',
  NumpadDivide: 'NUMPAD /',
  NumpadDecimal: 'NUMPAD .',
  NumpadEnter: 'NUMPAD ENTER',
};

function formatKeybindLabel(keybind: Keybind): string {
  const numpadLabel = keybind.code ? numpadCodeLabels[keybind.code] : undefined;
  const baseKeyLabel = numpadLabel ?? (keybind.key ?? '').toUpperCase();

  let modifiers = '';
  if (keybind.ctrlKey && keybind.key != 'Control') modifiers += 'Ctrl+';
  if (keybind.altKey && keybind.key != 'Alt') modifiers += 'Alt+';
  if (keybind.shiftKey && keybind.key != 'Shift') modifiers += 'Shift+';
  if (keybind.metaKey && keybind.key != 'Meta') modifiers += 'Meta+';

  return modifiers + baseKeyLabel;
}

function editKeybind(index: number, e: KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();

  if (formData.value[index]) {
    const newKeybind: Keybind = {
      key: e.key,
      code: e.code,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      shiftKey: e.shiftKey,
      metaKey: e.metaKey,
    };

    const keybind = formData.value[index].keybind;
    keybind.keyText = formatKeybindLabel(newKeybind);
    keybind.key = newKeybind.key;
    keybind.code = newKeybind.code;
    keybind.ctrlKey = newKeybind.ctrlKey;
    keybind.altKey = newKeybind.altKey;
    keybind.shiftKey = newKeybind.shiftKey;
    keybind.metaKey = newKeybind.metaKey;

    formData.value[index].changed = isChanged(formData.value[index]);
  }
}

  function stringifyFormData() {
    const keybinds: Record<KeyboardActions, Keybind> = formData.value.reduce(
      (acc, d) => {
        acc[d.action] = toKeybind(d.keybind);
        return acc;
      },
      {} as Record<KeyboardActions, Keybind>,
    );

    return JSON.stringify(keybinds);
  }

  function toKeybind(formKeybind: FormKeybind): Keybind {
    const { actionText, keyText, ...keybind } = formKeybind;
    return keybind;
  }

  const isFormChanged = computed(() => {
    if (mode.value == 'default') return false;
    if (isLocked.value !== isLockedInitial.value) return true;
    for (const entry of formData.value) {
      if (entry.changed) {
        return true;
      }
    }
    return false;
  });

  function isChanged(formAction: FormKeyboardAction) {
  if (!keybindings.value) return false;
  if (mode.value === 'default') return false;

  const savedKeybind = keybindings.value.find((action) => action[0] === formAction.action);
  Assert.definied(savedKeybind);

  return normalizeKeyString(savedKeybind[1]) !== normalizeKeyString(formAction.keybind);
}

  const typeSafeObjectEntries = <T extends Record<PropertyKey, unknown>>(
    obj: T,
  ): { [K in keyof T]: [K, T[K]] }[keyof T][] => {
    return Object.entries(obj) as { [K in keyof T]: [K, T[K]] }[keyof T][];
  };

function buildFormEntry(action: KeyboardActions, keybind: Keybind): FormKeyboardAction {
  return {
    action,
    keybind: {
      actionText: translation.value.enums.keyboardAction(action),
      keyText: formatKeybindLabel(keybind),
      key: keybind.key,
      code: keybind.code,
      ctrlKey: keybind.ctrlKey,
      altKey: keybind.altKey,
      shiftKey: keybind.shiftKey,
      metaKey: keybind.metaKey,
    },
    changed: false,
  };
}

  function updateFormData() {
    if (!keybindings.value) return;
    formData.value = keybindings.value.map(([action, keybind]) => buildFormEntry(action, keybind));
  }

  function resetToDefault() {
    formData.value = typeSafeObjectEntries(defaultBindings).map(([action, keybind]) => {
      const entry = buildFormEntry(action, keybind);
      entry.changed = isChanged(entry);
      return entry;
    });
  }

  // Adds or remove actions that's not part of the enum.
  function syncRecordWithEnum(
    record: Partial<Record<KeyboardActions, Keybind>>,
  ): Record<KeyboardActions, Keybind> {
    const result = {} as Record<KeyboardActions, Keybind>;

    for (const action of Object.values(KeyboardActions)) {
      const fromRecord = record[action];

      // Only keep values that belong to the enum
      if (fromRecord) {
        result[action] = fromRecord;
      } else {
        result[action] = JSON.parse(JSON.stringify(defaultBindings[action]));
      }
    }

    return result;
  }

  return {
    keyDownHandler,
    keyUpHandler,
    isActionHeld,
    clearActiveActions,
    parseKeybindings,
    editKeybind,
    stringifyFormData,
    updateFormData,
    resetToDefault,
    keyboardAction,
    activeActions,
    keybindings,
    bindings,
    isLocked,
    formData,
    isFormChanged,
  };
});
